import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { adminService, type AdminDiagnostics } from '@/services/adminService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';

export default function AdminDebug() {
  const { user, profile } = useAuth();
  const [diagnostics, setDiagnostics] = useState<AdminDiagnostics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function runDiagnostics() {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        const result = await adminService.getAdminDiagnostics(user, profile);
        setDiagnostics(result);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    
    runDiagnostics();
  }, [user, profile]);

  return (
      <div className="container max-w-4xl py-10">
        <h1 className="text-3xl font-black text-gray-900 mb-2">Admin Diagnostics</h1>
        <p className="text-gray-500 mb-8">Página de debug para verificar a fonte da verdade da autenticação de administrador.</p>
        
        {!user ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-gray-500 text-center py-10">Você precisa estar logado para ver o diagnóstico.</p>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : diagnostics ? (
          <div className="space-y-6">
            <Card className={diagnostics.isAdmin ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}>
              <CardHeader className="flex flex-row items-center gap-4">
                <div className={`p-3 rounded-full ${diagnostics.isAdmin ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {diagnostics.isAdmin ? <ShieldCheck className="h-6 w-6" /> : <ShieldAlert className="h-6 w-6" />}
                </div>
                <div>
                  <CardTitle className="text-xl">
                    Status Atual: {diagnostics.isAdmin ? 'Administrador' : 'Usuário Comum'}
                  </CardTitle>
                  <CardDescription>
                    Fonte de determinação: <strong className="uppercase">{diagnostics.sourceUsed}</strong>
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Dados Brutos (Raw Data)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1">E-mail Logado</p>
                    <p className="font-mono text-sm">{diagnostics.email || 'Nenhum'}</p>
                  </div>
                  
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1">User ID</p>
                    <p className="font-mono text-sm truncate">{diagnostics.userId || 'Nenhum'}</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1">Profile Role</p>
                    <Badge variant={diagnostics.profileRole === 'admin' ? 'default' : 'secondary'}>
                      {diagnostics.profileRole || 'null'}
                    </Badge>
                  </div>
                  
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1">App Metadata Role</p>
                    <Badge variant={diagnostics.appMetadataRole === 'admin' ? 'default' : 'secondary'}>
                      {diagnostics.appMetadataRole || 'null'}
                    </Badge>
                  </div>
                  
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1">User Metadata Role</p>
                    <Badge variant={diagnostics.userMetadataRole === 'admin' ? 'default' : 'secondary'}>
                      {diagnostics.userMetadataRole || 'null'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
  );
}
